import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Material UI
import {
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { blue } from "@material-ui/core/colors";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Components
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import RegularButton from "components/CustomButtons/Button";
import { SelectComponent } from "components/SelectMenu/SelectComponent";
import { Autocomplete } from "@material-ui/lab";

// Hooks
import { useAddLabel } from "hooks/useAddLabels";
import { useForm } from "hooks/useForm";
import { useToggle } from "hooks/useToggle";

// Redux Actions
import {
  cancelAddCycleMode,
  createCustomerCycle,
  deleteCustomerCycle,
  editCustomerCycle,
  getCustomerProducts,
  getSchedules,
} from "redux/actions/customers/customersAction";

// Helpers
import { animations } from "helpers/animations";
import { formatCustomDate, isValidCustomDate } from "helpers/dateTools";
import { validateObject } from "helpers/validateObject";

// Local
import { styleShare } from "../orders/OrderCard";
import { daysString, serviceDays } from "./cycleServices";
import { DaysCheckBox } from "./DaysCheckBox";
import { fieldValidations } from "./inputCycleValidations";
import { cyclesType, programmingTypes, programmingTypeOptions } from "./cycles.enum";
import { useSearch } from "hooks/useSearch";

const initialValues = {
  category: programmingTypes.PEDIDO,
  account: null,
  idProduct: "",
  quantity: "",
  idCyclePeriod: 1,
  idCycleType: null,
  description: formatCustomDate(new Date(), "-"),
  schedule: "",
  supplyDate: "",
  delivery: "",
  status: "ACTIVE"
}

const initialValuesSuscriptionType = {
  byDay: false,
  byNumberOfDays: false,
  byDate: false,
}

export const CustomerCycleForm = () => {
  const dispatch = useDispatch()
  const {
    cyclesTypes,
    cycle,
    products,
    addCycleMode,
    customer,
    schedules,
    byDateSchedules,
    supplyDates,
    deliveryPreferences
  } = useSelector(({ customersReducer }) => customersReducer)
  const { stationsList } = useSelector(({ companies }) => companies)
  const { brands } = useSelector(({ brandsReducer }) => brandsReducer)
  const {
    inputValues,
    handleInputOnChange,
    cleanForm,
    fieldErrors,
    handleNewValues,
    runValidates,
    checkboxOnChange,
  } = useForm(initialValues, fieldValidations)

  const {
    category,
    account,
    idProduct,
    quantity,
    idCycleType,
    period,
    description,
    schedule,
    supplyDate,
    delivery,
    status
  } = inputValues;

  const isCuenta = category === programmingTypes.CUENTA;

  const accountsMap = useAddLabel(customer?.accounts, "alias");
  const [handleSuscriptionType, setHandleSuscriptionType] = useState(
    initialValuesSuscriptionType
  );
  const { byDay, byNumberOfDays, byDate } = handleSuscriptionType;
  const [showCancell, setShowCancell, setControlledCancell] = useToggle();
  const [blockInput, setBlockInput] = useState(false);
  const [switchSchedules, setSwitchSchedules] = useState(false);
  const [days, setDays] = useState(serviceDays);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const { WEEK_DAYS, NUMBER_DAYS, DATE, NO_CYCLE } = cyclesType;

  const accountIdStation = account
    ? customer?.accounts?.find((acc) => acc.id === account.id)?.idStation
    : null;

  const brandOptions = useMemo(() => {
    if (!brands || !accountIdStation) return [];
    const uniqueBrands = Object.values(brands)
      .filter((brand) => brand.idStation === accountIdStation)
      .reduce((acc, brand) => {
        if (!acc.find((b) => b.idBrand === brand.idBrand)) acc.push(brand);
        return acc;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));
    return uniqueBrands;
  }, [brands, accountIdStation]);

  const { setSnackbar } = useSearch();
  const handleCancellAddMode = () => {
    dispatch(cancelAddCycleMode())
  }

  const CYCLE_TYPE_MAP = {
    '6b2493ea-ca8d-11ed-9f7b-02001702bbc9': 'WEEKS',  // DIAS DE SEMANA
    '6b24960d-ca8d-11ed-9f7b-02001702bbc9': 'MONTHS', // FECHA
    '6b2496d4-ca8d-11ed-9f7b-02001702bbc9': 'DAYS',   // NUMERO DE DIAS
    '6b24970d-ca8d-11ed-9f7b-02001702bbc9': null,      // SIN CICLO
  };

  const getTypeByCycleType = (cycleTypeObj) => {
    if (!cycleTypeObj?.id) return null;
    return CYCLE_TYPE_MAP[cycleTypeObj.id] ?? null;
  }

  const handleSubmit = async () => {
    // Validaciones personalizadas según el tipo
    if (!account?.id) {
      setSnackbar("Debe seleccionar una cuenta", "warning");
      return;
    }
    if (!selectedBrand?.idBrand) {
      setSnackbar("Debe seleccionar una marca", "warning");
      return;
    }
    if (!isCuenta && !idProduct) {
      setSnackbar("Debe seleccionar un producto", "warning");
      return;
    }
    
    if (!isCuenta && (!quantity || Number(quantity) <= 0)) {
      setSnackbar("Debe ingresar una cantidad en litros mayor a 0", "warning");
      return;
    }
    if (!idCycleType?.id) {
      setSnackbar("Debe seleccionar un tipo de suscripción", "warning");
      return;
    }
    if (!schedule) {
      setSnackbar("Debe seleccionar una hora", "warning");
      return;
    }
    if (!supplyDate) {
      setSnackbar("Debe seleccionar la fecha del primer servicio", "warning");
      return;
    }
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    if (supplyDate < todayString) {
      setSnackbar("La fecha del primer servicio debe ser hoy o posterior", "warning");
      return;
    }
    if (!byDay && !delivery) {
      setSnackbar("Debe seleccionar la preferencia de entrega", "warning");
      return;
    }
    if (!period || period < 1) {
      setSnackbar("El periodo debe ser mayor a 0", "warning");
      return;
    }

    const cycleId = cycle.id;
    
    let deliveryDays = [];
    let finalDescription = description.toString();
    
    if (byDay) {
      const isActive = days.filter((a) => a.checked);
      if (isActive.length === 0) {
        setSnackbar("Debe seleccionar al menos un dia", "warning");
        return;
      }
      deliveryDays = isActive.map((a) => a.value.toUpperCase());
    }

    const form = {
      category,
      idCycleType: idCycleType?.id,
      idStation: accountIdStation,
      idBrand: selectedBrand?.idBrand || null,
      schedule,
      description: finalDescription,
      type: getTypeByCycleType(idCycleType),
      period: Number(period) || 1,
      delivery: byDay ? null : delivery,
      deliveryDays: deliveryDays.length > 0 ? deliveryDays : null,
      supplyDate,
      status: status,
      ...(isCuenta ? {} : {
        idProduct,
        quantity: Number(quantity),
        quantityType: 'liters',
        paymentType: 'cash',
      }),
    };

    if (addCycleMode) {
      dispatch(createCustomerCycle(account?.id, form, cleanForm));
      cleanForm();
      handleNewValues(initialValues);
      setHandleSuscriptionType(initialValuesSuscriptionType);
      dispatch(cancelAddCycleMode());
      setDays(serviceDays);
      setSelectedBrand(null);
    } else {
      dispatch(editCustomerCycle(account?.id, cycleId, form));
    }
  };

  const cleanCancel = () => {
    cleanForm();
    setHandleSuscriptionType(initialValuesSuscriptionType);
    setShowCancell();
  };
  const handleCancelCycle = () => {
    const cycleId = cycle.id
    dispatch(deleteCustomerCycle(cycle.idAccount, cycleId, cleanCancel))
  }

  const handleCheckedDays = ({ target }) => {
    const { checked, name } = target
    const newValues = days.map((d) =>
      d.value === name ? { ...d, checked } : d
    );

    setDays(newValues);
  };

  const handleCycle = (v) => {
    handleNewValues({
      description: "",
      idCycleType: v,
    });
  };

  useEffect(() => {
    setBlockInput(false);
    if (addCycleMode) {
      cleanForm();
      setHandleSuscriptionType(initialValuesSuscriptionType);
      setDays(serviceDays);
      return;
    } else if (validateObject(cycle)) {
      const newDescription = cycle.description;
      
      if (cycle.deliveryDays) {
        let daysArray = [];
        if (Array.isArray(cycle.deliveryDays)) {
          daysArray = cycle.deliveryDays.flatMap(d => d.split(',').map(day => day.trim()));
        } else if (typeof cycle.deliveryDays === 'string') {
          daysArray = cycle.deliveryDays.split(',').map(d => d.trim());
        }
        
        if (daysArray.length > 0) {
          const updatedDays = serviceDays.map((day) => ({
            ...day,
            checked: daysArray.some(
              (deliveryDay) => deliveryDay.toLowerCase() === day.value.toLowerCase()
            ),
          }));
          setDays(updatedDays);
        }
      } else if (
        cycle.description.split(" ").some((day) =>
          daysString
            .split(" ")
            .map((d) => d.toLowerCase())
            .includes(day.toLowerCase())
        )
      ) {
        // Fallback: si viene en description
        const splitDays = cycle.description
          .split(" ")
          .map((d) => d.toLowerCase());
        const updatedDays = serviceDays.map((day) => {
          return {
            ...day,
            checked: splitDays.includes(day.value.toLowerCase()),
          };
        });
        setDays(updatedDays);
      }
      
      const clys = cyclesTypes.filter(
        (cl) => !cl.label.toLowerCase().includes("sin ")
      );

      const newValues = {
        category: cycle?.category || programmingTypes.PEDIDO,
        account: customer.accounts.find((acc) => acc.id === cycle?.idAccount),
        idProduct: cycle?.idProduct,
        quantity: cycle?.quantity,
        idCycleType: clys.find((cl) => cl.id === cycle?.idCycleType),
        description: newDescription,
        schedule: cycle?.schedule,
        period: cycle?.period,
        supplyDate: cycle?.supplyDate,
        delivery: cycle.delivery,
        date: formatCustomDate(cycle?.supplyDate, "-"),
        status: cycle?.status || "ACTIVE",
      }
      handleNewValues(newValues)
      if (cycle?.idBrand && brands) {
        const found = Object.values(brands).find((b) => b.idBrand === cycle.idBrand);
        setSelectedBrand(found || null);
      }
      setBlockInput(true)
      return
    }
    // eslint-disable-next-line
  }, [addCycleMode, cycle])

  useEffect(() => {
    if (idCycleType?.type) {
      const newKey = idCycleType.type.toLowerCase().replaceAll(" ", "")
      switch (newKey) {
        case "diasdesemana":
          return setHandleSuscriptionType({
            ...initialValuesSuscriptionType,
            byDay: true,
          })
        case "numerodedias":
          return setHandleSuscriptionType({
            ...initialValuesSuscriptionType,
            byNumberOfDays: true,
          })
        case "fecha":
          return setHandleSuscriptionType({
            ...initialValuesSuscriptionType,
            byDate: true,
          })
        case "sinciclo":
          return setHandleSuscriptionType({
            ...initialValuesSuscriptionType,
            byDate: true,
          })

        default:
          return undefined
      }
    }
    // eslint-disable-next-line
  }, [idCycleType])

  useEffect(() => {
    if (account?.id) {
      const findAccount = customer?.accounts?.find((acc) => acc.id === account.id)
      if (findAccount?.idStation) {
        dispatch(getSchedules(findAccount.idStation))
        dispatch(getCustomerProducts(account.id, {
          stations: stationsList,
          customerIdStation: findAccount.idStation
        }))
      }
    }
    setControlledCancell(false)
    // eslint-disable-next-line
  }, [account])

  useEffect(() => {
    if (idCycleType?.id && account) {
      const findCycle = cyclesTypes.find((cl) => cl.id === idCycleType.id)
      const findAccount = customer.accounts.find((acc) => acc.id === account?.id)
      if (
        findCycle.type.toLowerCase().includes(DATE) &&
        isValidCustomDate(description)
      ) {
        const newDescription =
          description.length < 3
            ? formatCustomDate(new Date(), "-")
            : description;
        dispatch(getSchedules(findAccount.idStation, newDescription));
        setSwitchSchedules(true);
      } else {
        setSwitchSchedules(false);
      }

      const suscription = cyclesTypes.find((cy) => cy.id === idCycleType?.id);
      if (suscription) {
        const newKey = suscription.type;
        switch (newKey) {
          case WEEK_DAYS:
            return setHandleSuscriptionType({
              byDate: false,
              byNumberOfDays: false,
              byDay: true,
            });
          case NUMBER_DAYS:
            return setHandleSuscriptionType({
              byDate: false,
              byDay: false,
              byNumberOfDays: true,
            });
          case DATE:
            return setHandleSuscriptionType({
              byDate: true,
              byNumberOfDays: false,
              byDay: false,
            });
          case NO_CYCLE:
            return setHandleSuscriptionType({
              byDate: true,
              byNumberOfDays: false,
              byDay: false,
            });

          default:
            return undefined;
        }
      }
    }
    // eslint-disable-next-line
  }, [idCycleType, description])

  return (
    <FormControl fullWidth>
      {addCycleMode && (
        <FormLabel
          style={{ color: blue[700], animationDelay: "1s" }}
          className={animations.fadeIn}
        >
          Programando nuevo ciclo
        </FormLabel>
      )}
      <Grid
        container
        spacing={1}
        style={{ marginTop: 24, opacity: showCancell ? 0.3 : 1 }}
      >
        <Grid item xs={12}>
          <SelectComponent
            label="Tipo de programación *"
            name="category"
            value={category}
            onChange={handleInputOnChange}
            options={programmingTypeOptions}
            defaultValue="Selecciona el tipo"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={status === "ACTIVE"}
                onChange={(e) => {
                  handleNewValues({ status: e.target.checked ? "ACTIVE" : "INACTIVE" });
                }}
                name="status"
                color="primary"
              />
            }
            label={status === "ACTIVE" ? "Activo" : "Inactivo"}
          />
        </Grid>
        <Grid item xs={12}>
          <AutocompleteComponent
            label="Cuenta *"
            name="account"
            value={account}
            handleValues={handleNewValues}
            options={accountsMap}
            textFieldProps={{
              error: fieldErrors.account,
              helperText: fieldErrors.account,
              disabled: blockInput,
            }}
          />
        </Grid>
        {account && (
          <Grid item xs={12}>
            <Autocomplete
              options={brandOptions}
              value={selectedBrand}
              getOptionLabel={(option) => option.name || ""}
              getOptionSelected={(option, value) => option.idBrand === value.idBrand}
              renderInput={(params) => (
                <TextField {...params} label="Marca *" variant="outlined" />
              )}
              fullWidth
              onChange={(e, value) => setSelectedBrand(value)}
            />
          </Grid>
        )}
        {!isCuenta && (
          <Grid item xs={12}>
            <SelectComponent
              label="Producto *"
              name="idProduct"
              value={idProduct}
              onChange={handleInputOnChange}
              options={products}
              defaultValue="Selecciona el producto"
              error={fieldErrors.idProduct}
            />
          </Grid>
        )}
        {!isCuenta && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Cantidad"
              name="quantity"
              type="number"
              value={quantity}
              InputProps={{
                endAdornment: <p>lts</p>,
              }}
              onChange={handleInputOnChange}
              variant="outlined"
              error={fieldErrors.quantity}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <AutocompleteComponent
            label="Tipo de suscripción *"
            name="idCycleType"
            value={idCycleType}
            customAction={handleCycle}
            options={cyclesTypes.filter(
              (cl) => {
                const label = cl.label.toLowerCase();
                return !label.includes("sin ") && 
                       !label.includes("cada x dias") && 
                       !label.includes("cada x meses") && 
                       !label.includes("cada x semanas");
              }
            )}
            defaultValue="Selecciona el tipo"
            error={fieldErrors.idCycleType}
          />
        </Grid>
        <Grid item xs={12}>
          {byDay && (
            <>
              <DaysCheckBox data={days} handleChange={handleCheckedDays} />
              <TextField
                fullWidth
                label="¿Cada cuántas semanas?"
                name="period"
                type="number"
                value={period}
                onChange={handleInputOnChange}
                InputProps={{ inputProps: { min: 1 } }}
                variant="outlined"
                error={fieldErrors.period}
                style={{ marginTop: 8 }}
              />
            </>
          )}
          {byNumberOfDays && (
            <TextField
              fullWidth
              label="¿Cada cuántos días?"
              name="period"
              type="number"
              value={period}
              onChange={handleInputOnChange}
              InputProps={{ inputProps: { min: 1 } }}
              variant="outlined"
              error={fieldErrors.period}
            />
          )}

          {byDate && (
            <>
              <TextField
                fullWidth
                label="¿Cada cuántos meses?"
                name="period"
                type="number"
                value={period}
                onChange={handleInputOnChange}
                InputProps={{ inputProps: { min: 1 } }}
                variant="outlined"
                error={fieldErrors.period}
                style={{ marginTop: 8 }}
              />
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <SelectComponent
            label="Hora *"
            name="schedule"
            value={schedule}
            onChange={handleInputOnChange}
            options={
              switchSchedules ? byDateSchedules : (schedules?.[0]?.schedules || [])
            }
            error={fieldErrors.schedule}
          />
        </Grid>
        <Grid item xs={12}>
          <DatePicker
            selected={supplyDate ? new Date(supplyDate + "T00:00:00") : null}
            onChange={(newDate) => {
              if (newDate) {
                const year = newDate.getFullYear();
                const month = String(newDate.getMonth() + 1).padStart(2, "0");
                const day = String(newDate.getDate()).padStart(2, "0");
                const formattedDate = `${year}-${month}-${day}`;
                handleNewValues({ supplyDate: formattedDate });
              }
            }}
            dateFormat="dd/MM/yyyy"
            minDate={new Date()}
            customInput={
              <TextField
                label="Primer servicio *"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={fieldErrors.supplyDate}
                InputProps={{
                  endAdornment: (
                    <span style={{ cursor: "pointer", color: "#666" }}>📅</span>
                  ),
                }}
              />
            }
          />
        </Grid>
        {!byDay && (
          <Grid item xs={12}>
            <SelectComponent
              label="Preferencia de entrega *"
              name="delivery"
              value={delivery}
              onChange={handleInputOnChange}
              options={deliveryPreferences}
              defaultValue="Selecciona la preferencia"
              error={fieldErrors.delivery}
            />
          </Grid>
        )}
      </Grid>
      {showCancell ? (
        <div style={{ width: "50%", margin: "0 auto", textAlign: "center" }}>
          <Typography variant="subtitle2" align="center">
            desea cancelar este ciclo?
          </Typography>
          <div style={{ ...styleShare, gap: 4 }}>
            <RegularButton color="danger" onClick={setShowCancell}>
              no
            </RegularButton>
            <RegularButton color="info" onClick={handleCancelCycle}>
              si
            </RegularButton>
          </div>
        </div>
      ) : (
        <>
          <RegularButton color="info" fullWidth onClick={handleSubmit}>
            {addCycleMode ? "Crear Ciclo" : "Guardar Ciclo"}
          </RegularButton>
          <RegularButton
            color={addCycleMode ? "danger" : "warning"}
            fullWidth
            onClick={addCycleMode ? handleCancellAddMode : setShowCancell}
          >
            {addCycleMode ? "Cancelar" : "Cancelar Ciclo"}
          </RegularButton>
        </>
      )}
    </FormControl>
  )
}
